/**
 * ChatContextPoolStrip — entity/asset chips for chat context.
 */
import { useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import { useAppStore } from '@/store/appStore';
import { Music, Building2, Users, Calendar, Image } from 'lucide-react';

const ENTITY_ICONS: Record<string, React.ElementType> = {
  artist: Music,
  venue: Building2,
  promoter: Users,
  event: Calendar,
};

type Props = {
  onInsertEntity?: (id: Id<'entities'>, name: string) => void;
  onInsertAsset?: (id: Id<'assets'>, name: string) => void;
};

export function ChatContextPoolStrip({ onInsertEntity, onInsertAsset }: Props) {
  const accountId = useAppStore((s) => s.currentAccountId) ?? undefined;
  const entities = useQuery(api.entities.list, { accountId });
  const assets = useQuery(api.assets.list, { accountId });

  if ((!entities || entities.length === 0) && (!assets || assets.length === 0)) return null;

  return (
    <div className="flex flex-wrap gap-1.5 p-2 border-t border-border">
      {entities?.slice(0, 8).map((e) => {
        const Icon = ENTITY_ICONS[e.entityType] ?? Users;
        return (
          <button
            key={e._id}
            type="button"
            onClick={() => onInsertEntity?.(e._id, e.displayName ?? e.name)}
            className="flex items-center gap-1.5 rounded-full bg-muted/80 px-2.5 py-1 text-xs font-medium hover:bg-muted transition-colors"
          >
            <Icon className="h-3 w-3 text-muted-foreground" />
            <span className="truncate max-w-[100px]">{e.displayName ?? e.name}</span>
          </button>
        );
      })}
      {assets?.slice(0, 4).map((a) => (
        <button
          key={a._id}
          type="button"
          onClick={() => onInsertAsset?.(a._id, a.name)}
          className="flex items-center gap-1.5 rounded-full bg-muted/80 px-2.5 py-1 text-xs font-medium hover:bg-muted transition-colors"
        >
          <Image className="h-3 w-3 text-muted-foreground" />
          <span className="truncate max-w-[80px]">{a.name}</span>
        </button>
      ))}
    </div>
  );
}
